import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { StoreService } from "@/gen/omnios/v1/store_connect";
import { transport } from "@/lib/grpc";
import { cn } from "@/lib/utils";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { FileText, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/portals/$id/policies")({
	component: PortalPoliciesPage,
});

const storeClient = createClient(StoreService, transport);

const POLICY_TYPES = [
	"return_policy",
	"shipping_policy",
	"privacy_policy",
	"terms_of_service",
	"payment_terms",
] as const;

function PortalPoliciesPage() {
	const { id: portalId } = useParams({ from: "/portals/$id/policies" });
	const { t } = useTranslation("commerce");
	const qc = useQueryClient();

	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [showCreate, setShowCreate] = useState(false);
	const [showDelete, setShowDelete] = useState(false);

	// Editor state
	const [editTitle, setEditTitle] = useState("");
	const [editContent, setEditContent] = useState("");
	const [editActive, setEditActive] = useState(true);

	const { data, isLoading } = useQuery({
		queryKey: ["policies", portalId],
		queryFn: () =>
			storeClient.listPolicies({ portalInstanceId: portalId }),
	})

	const policies = data?.policies ?? [];
	const selected = policies.find((p) => p.id === selectedId) ?? null;

	// Sync editor when selection changes
	useEffect(() => {
		if (selected) {
			setEditTitle(selected.title);
			setEditContent(selected.contentHtml);
			setEditActive(selected.isActive);
		}
	}, [selected]);

	// Auto-select first
	useEffect(() => {
		if (!selectedId && policies.length > 0) {
			setSelectedId(policies[0].id);
		}
	}, [selectedId, policies]);

	// Update
	const updateMut = useMutation({
		mutationFn: () =>
			storeClient.updatePolicy({
				id: selectedId!,
				title: editTitle,
				contentHtml: editContent,
				isActive: editActive,
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["policies", portalId] });
			toast.success(t("policy_saved", { defaultValue: "Сохранено" }));
		},
	})

	// Delete
	const deleteMut = useMutation({
		mutationFn: () => storeClient.deletePolicy({ id: selectedId! }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["policies", portalId] });
			setSelectedId(null);
			setShowDelete(false);
		},
	})

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<h2 className="text-lg font-semibold">{t("policies_title")}</h2>
				<Button size="sm" className="gap-1.5" onClick={() => setShowCreate(true)}>
					<Plus className="h-3.5 w-3.5" />
					{t("create_policy")}
				</Button>
			</div>

			{isLoading && (
				<p className="py-8 text-center text-sm text-muted-foreground">
					{t("common:loading", { defaultValue: "Загрузка..." })}
				</p>
			)}

			{!isLoading && policies.length === 0 && (
				<div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 px-6">
					<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted mb-4">
						<FileText className="h-5 w-5 text-muted-foreground" />
					</div>
					<p className="text-sm text-muted-foreground">{t("no_policies")}</p>
				</div>
			)}

			{policies.length > 0 && (
				<div className="grid grid-cols-1 lg:grid-cols-4 gap-4 min-h-[400px]">
					{/* Left list */}
					<div className="lg:col-span-1 space-y-1 rounded-lg border p-2">
						{policies.map((p) => (
							<Button
								key={p.id}
								variant="ghost"
								onClick={() => setSelectedId(p.id)}
								className={cn(
									"w-full text-left justify-start h-auto rounded-md px-3 py-2",
									selectedId === p.id && "bg-accent",
								)}
							>
								<div className="flex items-center justify-between gap-2">
									<span className="truncate font-medium">{p.title}</span>
									{p.isActive ? (
										<Badge variant="default" className="shrink-0 text-[10px] px-1.5 py-0">
											{t("policy_active")}
										</Badge>
									) : (
										<Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0">
											{t("policy_inactive", { defaultValue: "Выкл" })}
										</Badge>
									)}
								</div>
								<p className="text-xs text-muted-foreground mt-0.5">
									{t(`pt_${p.type}`, { defaultValue: p.type })}
								</p>
							</Button>
						))}
					</div>

					{/* Right editor */}
					<div className="lg:col-span-3 rounded-lg border p-4 space-y-4">
						{selected ? (
							<>
								<div className="flex items-center justify-between gap-3">
									<div className="flex-1">
										<Label>{t("policy_name")}</Label>
										<Input
											value={editTitle}
											onChange={(e) => setEditTitle(e.target.value)}
											className="mt-1"
										/>
									</div>
									<div className="flex items-center gap-2 pt-5">
										<Switch
											checked={editActive}
											onCheckedChange={setEditActive}
										/>
										<span className="text-sm">{t("policy_active")}</span>
									</div>
								</div>

								<div>
									<Label>{t("policy_content")}</Label>
									<Textarea
										value={editContent}
										onChange={(e) => setEditContent(e.target.value)}
										rows={14}
										className="mt-1 font-mono text-sm"
									/>
								</div>

								<div className="flex items-center justify-between">
									<Button
										variant="destructive"
										size="sm"
										onClick={() => setShowDelete(true)}
									>
										<Trash2 className="h-3.5 w-3.5 mr-1" />
										{t("delete_policy")}
									</Button>
									<Button
										size="sm"
										className="gap-1.5"
										onClick={() => updateMut.mutate()}
										disabled={updateMut.isPending}
									>
										{updateMut.isPending ? (
											<Loader2 className="h-3.5 w-3.5 animate-spin" />
										) : (
											<Save className="h-3.5 w-3.5" />
										)}
										{t("save_policy")}
									</Button>
								</div>
							</>
						) : (
							<div className="flex items-center justify-center h-full text-sm text-muted-foreground">
								{t("select_policy", {
									defaultValue: "Выберите политику из списка",
								})}
							</div>
						)}
					</div>
				</div>
			)}

			{/* Create dialog */}
			<CreatePolicyDialog
				portalId={portalId}
				open={showCreate}
				onOpenChange={setShowCreate}
				onCreated={(id) => setSelectedId(id)}
			/>

			{/* Delete confirmation */}
			<AlertDialog open={showDelete} onOpenChange={setShowDelete}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("delete_policy")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("del_action_irreversible")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t("common:cancel", { defaultValue: "Отмена" })}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => deleteMut.mutate()}
							className="bg-destructive text-destructive-foreground"
							disabled={deleteMut.isPending}
						>
							{deleteMut.isPending && (
								<Loader2 className="h-4 w-4 mr-1 animate-spin" />
							)}
							{t("delete_policy")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}

function CreatePolicyDialog({
	portalId,
	open,
	onOpenChange,
	onCreated,
}: {
	portalId: string;
	open: boolean;
	onOpenChange: (v: boolean) => void;
	onCreated: (id: string) => void;
}) {
	const { t } = useTranslation("commerce");
	const qc = useQueryClient();
	const [title, setTitle] = useState("");
	const [type, setType] = useState<string>(POLICY_TYPES[0]);

	const createMut = useMutation({
		mutationFn: () =>
			storeClient.createPolicy({
				portalInstanceId: portalId,
				type,
				title: title || t(`pt_${type}`, { defaultValue: type }),
				contentHtml: "",
			}),
		onSuccess: (policy) => {
			qc.invalidateQueries({ queryKey: ["policies", portalId] });
			onOpenChange(false);
			setTitle("");
			onCreated(policy.id);
		},
	})

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t("create_policy")}</DialogTitle>
				</DialogHeader>
				<div className="space-y-3">
					<div>
						<Label>{t("policy_type", { defaultValue: "Тип" })}</Label>
						<Select value={type} onValueChange={setType}>
							<SelectTrigger className="mt-1">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{POLICY_TYPES.map((pt) => (
									<SelectItem key={pt} value={pt}>
										{t(`pt_${pt}`)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div>
						<Label>{t("policy_name")}</Label>
						<Input
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							placeholder={t(`pt_${type}`)}
							className="mt-1"
						/>
					</div>
				</div>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={createMut.isPending}
					>
						{t("common:cancel", { defaultValue: "Отмена" })}
					</Button>
					<Button
						onClick={() => createMut.mutate()}
						disabled={createMut.isPending}
					>
						{createMut.isPending && (
							<Loader2 className="h-4 w-4 mr-1 animate-spin" />
						)}
						{t("create_policy")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
